import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Bike as BikeIcon, CalendarDays, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, downloadContract, euro, fmtDays, fmtRange, imageUri } from "@/lib/noleggio";
import { FileDown } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import ShopContact from "@/components/ShopContact";
import { StatusBadges } from "@/components/StatusBadges";

export default function Bookings() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [toCancel, setToCancel] = useState(null);
  const q = useQuery({ queryKey: ["my-bookings", user?.id], queryFn: () => api("/bookings/me"), enabled: !!user });
  const cancel = useMutation({
    mutationFn: (id) => api(`/bookings/${id}/cancel`, { method: "POST" }),
    onSuccess: () => { toast.success(t("booking_cancelled")); qc.invalidateQueries(); },
    onError: (e) => toast.error(e.message),
    onSettled: () => setToCancel(null),
  });

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 pt-8">
      <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4">{t("my_bookings")}</h1>
      <ShopContact prefix="bookings" />
      {loading || (user && q.isLoading) ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin" color="var(--brand-primary)" /></div>
      ) : !user ? (
        <Empty icon={CalendarDays} title={t("login_to_see")}>
          <button data-testid="bookings-login-button" className="mn-btn" onClick={() => nav("/accedi?next=/prenotazioni")}>{t("login")}</button>
        </Empty>
      ) : (q.data ?? []).length === 0 ? (
        <Empty icon={BikeIcon} title={t("no_bookings")} sub={t("no_bookings_sub")}>
          <button data-testid="bookings-browse-button" className="mn-btn mn-btn-ghost" onClick={() => nav("/")}>{t("browse_bikes")}</button>
        </Empty>
      ) : (
        <div data-testid="bookings-list" className="flex flex-col gap-3 mt-6">
          {q.data.map((b) => {
            const active = b.status === "confirmed" || b.status === "pending_payment";
            return (
              <div key={b.id} data-testid={`booking-card-${b.id}`} className="mn-card p-4 flex flex-col gap-3 mn-rise">
                <div className="flex gap-3 items-center">
                  <img src={imageUri(b.bike_image)} alt="" className="w-16 h-16 rounded-xl object-cover bg-[var(--surface-3)]" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{b.bike_name}</p>
                    <p className="text-sm text-[var(--text-2)]">{t("size_x", { s: b.size })} · {fmtDays(b.units)}{b.unit_id ? ` · ${t("assigned_unit", { unit: b.unit_id })}` : ""}</p>
                    <p className="text-sm text-[var(--text-2)]">{fmtRange(b)}</p>
                  </div>
                  <p className="font-extrabold">{euro(b.total)}</p>
                </div>
                <StatusBadges booking={b} />
                <div className="flex gap-2">
                  {b.contract_version && (
                    <button data-testid={`booking-contract-${b.id}`} className="mn-btn mn-btn-ghost flex-1" onClick={() => downloadContract(b).catch((e) => toast.error(e.message))}>
                      <FileDown size={17} /> {t("contract_pdf")}
                    </button>
                  )}
                  {active && <button data-testid={`booking-cancel-${b.id}`} className="mn-btn mn-btn-ghost flex-1" onClick={() => setToCancel(b)}>{t("cancel")}</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {toCancel && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center" onClick={() => setToCancel(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-6 flex flex-col gap-3 mn-rise">
            <h2 className="text-xl font-extrabold">{t("cancel_q")}</h2>
            <p className="text-sm text-[var(--muted)]">{toCancel.bike_name} · {fmtRange(toCancel)}</p>
            <button data-testid="confirm-cancel-button" className="mn-btn mn-btn-danger" disabled={cancel.isPending} onClick={() => cancel.mutate(toCancel.id)}>
              {cancel.isPending && <Loader2 size={18} className="animate-spin" />}{t("yes_cancel")}
            </button>
            <button data-testid="dismiss-cancel-button" className="mn-btn mn-btn-ghost" onClick={() => setToCancel(null)}>{t("no_keep")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Empty({ icon: Icon, title, sub, children }) {
  return (
    <div className="py-16 flex flex-col items-center gap-3 text-center">
      <Icon size={48} color="var(--muted)" />
      <p className="text-lg font-bold">{title}</p>
      {sub && <p className="text-sm text-[var(--muted)]">{sub}</p>}
      {children}
    </div>
  );
}
